import React, { useEffect, useState } from 'react'
import { ActivityIndicator, ScrollView } from 'react-native'

import { useRoute } from '@react-navigation/native'
import { publishEvent } from '@zet/analytics'
import { useAgentDetailsWF } from '@zet/customer'
import useAddCustomerStore from '@zet/customer/src/store/addCustomer'
import { BottomSheet, Button, Stack } from '@zet/design-system'
import { i18n } from '@zet/localization'
import _ from 'lodash'

import { useBrandDetailsById } from '../../api/brands/hooks'
import { BottomsheetTypes } from '../../api/brands/types'
import DontHaveAccesstoNumbers from './components/DontHaveAccessToNumbers'
import { ApplicationProcess } from './components/applicationProcess'
import { BasicDetails } from './components/basicDetails'
import { CardImage } from './components/cardImage'
import { EmploymentDetails } from './components/employmentDetails'
import { KeyBenefits } from './components/keyBenefits'
import OtpSecondaryPhVerification from './components/otpSecondaryPhVerification'
import { OtpVerification } from './components/otpVerification'
import { PersonalDetails } from './components/personalDetails'
import SecondaryPhoneNumbers from './components/secondaryPhoneNumbers'

const ProductPageWebflow = () => {
  const [bottomSheetState, setBottomsheetState] =
    useState<BottomsheetTypes>(null)
  const [bottomSheetProps, setBottomsheetProps] = useState<any>(null)
  const { data: agentData, isSuccess, isLoading } = useAgentDetailsWF()
  const isMagnetFlow = agentData?.partnerId === 193
  const isDsaFlow = agentData?.isDsa
  const { data: content } = useBrandDetailsById(agentData?.partnerId as number)
  const brandBenefits = content?.brandBenefits
  const store = useAddCustomerStore()
  const { params } = useRoute()

  function clearBottomsheetState() {
    setBottomsheetState(null)
    setBottomsheetProps(null)
  }
  function handleBottomsheetState(state: BottomsheetTypes, props?: any) {
    if (props) {
      setBottomsheetProps(props)
    }
    setBottomsheetState(state)
  }

  function renderBottomsheetsForm() {
    if (agentData) {
      switch (bottomSheetState) {
        case 'PERSONAL_DETAILS':
          return {
            form: (
              <PersonalDetails
                agentData={{ ...agentData, advisorCode: params?.advisorCode }}
                handleBottomsheetState={handleBottomsheetState}
              />
            ),
            snapPoints:
              isMagnetFlow && isDsaFlow
                ? [550, 550]
                : isMagnetFlow
                ? [450, 450]
                : isDsaFlow
                ? [650, 650]
                : [600, 600],
          }
        case 'OTP_VERIFICATION':
          return {
            form: (
              <OtpVerification
                agentData={agentData}
                handleBottomsheetState={handleBottomsheetState}
                data={bottomSheetProps}
              />
            ),
            snapPoints: [350, 350],
          }
        case 'BASIC_DETAILS':
          return {
            form: (
              <BasicDetails
                agentData={agentData}
                handleBottomsheetState={handleBottomsheetState}
              />
            ),
            snapPoints: [400, 400],
          }
        case 'EMPLOYMENT_DETAILS':
          return {
            form: (
              <EmploymentDetails
                agentData={agentData}
                handleBottomsheetState={handleBottomsheetState}
              />
            ),
            snapPoints: [700, 700],
          }
        case 'OTP_SECONDARY_PHONE_VERIFICATION':
          return {
            form: (
              <OtpSecondaryPhVerification
                agentData={agentData}
                handleBottomsheetState={handleBottomsheetState}
                data={bottomSheetProps}
              />
            ),
            snapPoints: [600, 600],
          }

        case 'SECONDARY_PHONE_NUMBERS':
          return {
            form: (
              <SecondaryPhoneNumbers
                agentData={agentData}
                handleBottomsheetState={handleBottomsheetState}
                data={bottomSheetProps}
              />
            ),
            snapPoints: [700, 700],
          }
        case 'DONT_HAVE_ACCESS_TO_NUMBERS':
          return {
            form: (
              <DontHaveAccesstoNumbers
                agentData={agentData}
                handleBottomsheetState={handleBottomsheetState}
              />
            ),
            snapPoints: [600, 600],
          }
        default:
          return { form: <></>, snapPoints: [0] }
      }
    } else return <></>
  }

  function handleOpenBottomsheet() {
    publishEvent('click_action', {
      item_name: 'shortflow_brand_page_apply',
    })
    handleBottomsheetState('PERSONAL_DETAILS')
  }
  useEffect(() => {
    publishEvent('screen_view', {
      screen_name: 'shortflow_brand_page',
    })
  }, [])

  useEffect(() => {
    i18n.locale = store.webFlowLocale
  }, [store.webFlowLocale])

  useEffect(() => {
    if (
      !_.isNull(agentData) &&
      !agentData?.isShortendCustomerJourney &&
      isSuccess
    ) {
      const customerLoginPathName = ['/customer/product']
      if (customerLoginPathName.includes(window?.location?.pathname)) {
        window.location.replace(
          `${window.location.origin}/customer/login${window?.location?.search}`
        )
      }
    }
  }, [isSuccess, agentData?.isShortendCustomerJourney, agentData])

  if (isLoading) return <ActivityIndicator size={'large'} />

  return (
    <Stack className="flex-1 pb-5">
      <ScrollView showsHorizontalScrollIndicator={false}>
        <CardImage content={content} />
        <KeyBenefits content={content} brandBenefits={brandBenefits} />
        <ApplicationProcess content={content} />
        <BottomSheet
          visible={!!bottomSheetState}
          onDismiss={clearBottomsheetState}
          snapPoints={renderBottomsheetsForm()?.snapPoints}
          eventPayload={bottomSheetProps}
        >
          {renderBottomsheetsForm()?.form}
        </BottomSheet>
      </ScrollView>
      <Stack className="bg-neutral-white100 border-grays-40 absolute bottom-0 w-full border-t px-3 py-2">
        <Button
          onPress={handleOpenBottomsheet}
          className="bg-tertiary-200"
          size={'lg'}
        >
          {i18n.t('brandPage.applyNow')}
        </Button>
      </Stack>
    </Stack>
  )
}

export default ProductPageWebflow
